import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

// Wheel themes
const wheelThemes = {
  classic: {
    borderColor: '#ffffff',
    textColor: '#ffffff',
    pointerColor: '#ffffff',
    fontFamily: "'Poppins', sans-serif",
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)',
    wheelShadow: '0 0 20px rgba(0, 0, 0, 0.5)'
  },
  neon: {
    borderColor: '#00ffff',
    textColor: '#ffff00',
    pointerColor: '#ff00ff',
    fontFamily: "'Press Start 2P', cursive",
    textShadow: '0 0 8px rgba(255, 255, 0, 0.8)',
    wheelShadow: '0 0 30px rgba(0, 255, 255, 0.5)'
  },
  pastel: {
    borderColor: '#fbf8cc',
    textColor: '#555555',
    pointerColor: '#f28482',
    fontFamily: "'Quicksand', sans-serif",
    textShadow: 'none',
    wheelShadow: '0 0 20px rgba(200, 200, 200, 0.5)'
  },
  monochrome: {
    borderColor: '#ffffff',
    textColor: '#ffffff',
    pointerColor: '#ffffff',
    fontFamily: "'Roboto Mono', monospace",
    textShadow: 'none',
    wheelShadow: '0 0 25px rgba(100, 100, 100, 0.5)'
  },
  galaxy: {
    borderColor: '#9b59b6',
    textColor: '#ffffff',
    pointerColor: '#e74c3c',
    fontFamily: "'Orbitron', sans-serif",
    textShadow: '0 0 10px rgba(155, 89, 182, 0.8)',
    wheelShadow: '0 0 40px rgba(155, 89, 182, 0.6)'
  },
  ocean: {
    borderColor: '#3498db',
    textColor: '#ffffff',
    pointerColor: '#2ecc71',
    fontFamily: "'Nunito', sans-serif",
    textShadow: '1px 1px 3px rgba(52, 152, 219, 0.7)',
    wheelShadow: '0 0 35px rgba(52, 152, 219, 0.5)'
  },
  fire: {
    borderColor: '#e74c3c',
    textColor: '#ffffff',
    pointerColor: '#f39c12',
    fontFamily: "'Rajdhani', sans-serif",
    textShadow: '0 0 6px rgba(231, 76, 60, 0.9)',
    wheelShadow: '0 0 30px rgba(231, 76, 60, 0.6)'
  },
  forest: {
    borderColor: '#27ae60',
    textColor: '#ffffff',
    pointerColor: '#f1c40f',
    fontFamily: "'Merriweather', serif",
    textShadow: '1px 1px 2px rgba(39, 174, 96, 0.8)',
    wheelShadow: '0 0 25px rgba(39, 174, 96, 0.5)'
  },
  retro: {
    borderColor: '#ff6b6b',
    textColor: '#2c3e50',
    pointerColor: '#4ecdc4',
    fontFamily: "'Fredoka One', cursive",
    textShadow: 'none',
    wheelShadow: '0 0 20px rgba(255, 107, 107, 0.4)'
  }
};

// Size configurations
const wheelSizes = {
  small: {
    width: 300,
    height: 300,
    fontSize: 12,
    pointerSize: 20
  },
  medium: {
    width: 400,
    height: 400,
    fontSize: 14,
    pointerSize: 25
  },
  large: {
    width: 500,
    height: 500,
    fontSize: 16,
    pointerSize: 30
  }
};

const WheelContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  width: ${props => wheelSizes[props.size]?.width || 400}px;
  height: ${props => wheelSizes[props.size]?.height || 400}px;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    height: auto;
    aspect-ratio: 1;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    max-width: 280px;
    margin-bottom: 10px;
  }
`;

const Canvas = styled.canvas`
  max-width: 100%;
  height: auto;
  border-radius: 50%;
  border: 10px solid ${props => wheelThemes[props.theme]?.borderColor || '#ffffff'};
  box-shadow: ${props => wheelThemes[props.theme]?.wheelShadow || '0 0 20px rgba(0, 0, 0, 0.5)'};
  transition: transform 0.2s ease;
  transform: rotate(0deg); /* Set default rotation */
  will-change: transform; /* Optimize for animation performance */
  backface-visibility: hidden; /* Prevent flickering */
  transform-style: preserve-3d; /* Improve performance */
  perspective: 1000px; /* Enhance 3D effect */
  
  ${props => props.isSpinning ? `
    animation: spin-wheel-${props.animationId} 6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards !important;
    
    @keyframes spin-wheel-${props.animationId} {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(${props.spinDegrees}deg);
      }
    }
  ` : `
    transform: rotate(0deg);
    animation: none;
  `}
`;

const Pointer = styled.div`
  position: absolute;
  top: -${props => wheelThemes[props.theme]?.pointerSize || 25}px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: ${props => wheelThemes[props.theme]?.pointerSize / 2 || 12.5}px solid transparent;
  border-right: ${props => wheelThemes[props.theme]?.pointerSize / 2 || 12.5}px solid transparent;
  border-top: ${props => wheelThemes[props.theme]?.pointerSize || 25}px solid ${props => wheelThemes[props.theme]?.pointerColor || '#ffffff'};
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.5));
  z-index: 2;
`;

const SpinningWheel = ({ 
  segments = [], 
  isSpinning = false, 
  size = 'medium', 
  theme = 'classic',
  spinResult = null,
  resetWheel = false
}) => {
  const canvasRef = useRef(null);
  const [spinDegrees, setSpinDegrees] = useState(7200);
  const [animationId, setAnimationId] = useState(Date.now()); // Unique ID for each animation
  
  // Add animation timing verification
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const handleAnimationStart = () => {
      console.log('🎬 Wheel animation STARTED at:', new Date().toLocaleTimeString());
    };
    
    const handleAnimationEnd = () => {
      console.log('🏁 Wheel animation ENDED at:', new Date().toLocaleTimeString());
      // Ensure the wheel stays at the final position
      if (canvas) {
        // Set final position without bounce (simpler and more reliable)
        canvas.style.transform = `rotate(${spinDegrees}deg)`;
        canvas.style.animation = 'none';
        canvas.style.transition = 'none';
      }
    };
    
    canvas.addEventListener('animationstart', handleAnimationStart);
    canvas.addEventListener('animationend', handleAnimationEnd);
    canvas.addEventListener('animationcancel', handleAnimationEnd);
    
    return () => {
      canvas.removeEventListener('animationstart', handleAnimationStart);
      canvas.removeEventListener('animationend', handleAnimationEnd);
      canvas.removeEventListener('animationcancel', handleAnimationEnd);
    };
  }, [spinDegrees]);
  
  // Calculate the final rotation based on the selected segment
  useEffect(() => {
    console.log('🎡 SpinningWheel useEffect triggered:', { 
      isSpinning, 
      segmentsCount: segments.length, 
      spinResult,
      segmentsInOrder: segments.map((s, i) => `${i}: "${s.text}"`),
      segmentTextsOnly: segments.map(s => s.text)
    });
    
    if (isSpinning && segments.length > 0) {
      // Generate new animation ID for each spin - this forces a CSS animation refresh
      const newAnimationId = Date.now();
      setAnimationId(newAnimationId);
      
      // Force reset of transform to ensure animation starts cleanly
      const canvas = canvasRef.current;
      if (canvas) {
        // Reset transform and animation
        canvas.style.transform = 'rotate(0deg)';
        canvas.style.animation = 'none';
        
        // Force a browser reflow to ensure animation restart
        void canvas.offsetWidth;
        void canvas.offsetHeight; // Additional force reflow for some browsers
        
        // Wait for next frame to ensure the DOM has updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Remove the temporary styles to allow animation to start
            canvas.style.animation = '';
          });
        });
      }
      
      if (spinResult) {
        // Find the index of the selected segment
        const selectedIndex = segments.findIndex(segment => 
          segment.text === spinResult || segment.text.trim() === spinResult.trim()
        );
        
        console.log('🎯 SpinningWheel calculation START:', {
          spinResult,
          selectedIndex,
          segments: segments.map((s, i) => `${i}: "${s.text}"`),
          totalSegments: segments.length,
          timestamp: new Date().toLocaleTimeString()
        });
        
        if (selectedIndex !== -1) {
          // Calculate the angle size of each segment
          const segmentAngle = 360 / segments.length;
          
          // Calculate where the selected segment's center is located
          const segmentCenter = selectedIndex * segmentAngle + (segmentAngle / 2);
          
          // To make the segment land under the pointer (top), we need to rotate
          // so that segmentCenter ends up at 270° (top of circle)
          const requiredRotation = 270 - segmentCenter;
          
          // Add multiple full rotations for the spinning effect
          const baseRotations = 10;
          const finalRotation = baseRotations * 360 + requiredRotation;
          
          setSpinDegrees(finalRotation);
          console.log(`✅ Wheel calculation COMPLETE:`, {
            spinResult,
            selectedIndex,
            segmentAngle: segmentAngle.toFixed(2),
            segmentCenter: segmentCenter.toFixed(2),
            requiredRotation: requiredRotation.toFixed(2),
            finalRotation: finalRotation.toFixed(2),
            shouldLandOn: `Segment ${selectedIndex}: "${spinResult}"`,
            preciseLanding: `Segment center ${segmentCenter.toFixed(1)}° will land exactly at pointer (270°)`
          });
        } else {
          console.error(`❌ Could not find segment "${spinResult}" in:`, segments.map(s => s.text));
          // For safety, land on the first segment if we can't find the exact match
          const segmentAngle = 360 / segments.length;
          const segmentMiddle = segmentAngle / 2;
          const baseRotations = 10;
          const finalRotation = (baseRotations * 360) + (360 - segmentMiddle);
          setSpinDegrees(finalRotation);
          console.log(`🔄 Fallback: Landing on first segment at ${finalRotation}°`);
        }
      } else {
        // If spinning but no result yet, provide a fixed animation
        const baseRotations = 10;
        const defaultRotation = baseRotations * 360;
        setSpinDegrees(defaultRotation);
        console.log(`🎲 No result yet, using default rotation: ${defaultRotation}° (${baseRotations} rotations)`);
      }
    } else if (!isSpinning) {
      // Reset wheel if explicitly requested or if no previous spin result
      if (resetWheel || spinDegrees === 0) {
        console.log('🔄 Resetting wheel to starting position');
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.transform = 'rotate(0deg)';
          canvas.style.animation = 'none';
        }
        if (spinDegrees !== 0) {
          setSpinDegrees(0);
        }
      } else {
        // Keep the wheel at its final position
        console.log('🛑 Wheel stopped, keeping final position at', spinDegrees, '°');
        
        // Ensure the canvas stays at the final position
        const canvas = canvasRef.current;
        if (canvas && spinDegrees > 0) {
          canvas.style.transform = `rotate(${spinDegrees}deg)`;
          canvas.style.animation = 'none';
        }
      }
    }
  }, [isSpinning, segments, spinResult, spinDegrees]);
  
  // Handle explicit wheel reset
  useEffect(() => {
    if (resetWheel) {
      console.log('🔄 Explicit wheel reset requested');
      setSpinDegrees(0);
      setAnimationId(Date.now());
      
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.transform = 'rotate(0deg)';
        canvas.style.animation = 'none';
      }
    }
  }, [resetWheel]);
  
  useEffect(() => {
    if (!segments || segments.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const wheelSize = wheelSizes[size] || wheelSizes.medium;
    const themeSettings = wheelThemes[theme] || wheelThemes.classic;
    
    // Set canvas dimensions
    canvas.width = wheelSize.width;
    canvas.height = wheelSize.height;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10; // Adjust for border
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Calculate angle for each segment
    const totalSegments = segments.length;
    const angle = (2 * Math.PI) / totalSegments;
    
    // Draw segments
    segments.forEach((segment, index) => {
      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(
        centerX,
        centerY,
        radius,
        index * angle,
        (index + 1) * angle
      );
      ctx.closePath();
      
      // Fill segment
      ctx.fillStyle = segment.color || '#4ecdc4';
      ctx.fill();
      
      // Add white border between segments
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(
        centerX,
        centerY,
        radius,
        index * angle,
        (index + 1) * angle
      );
      ctx.closePath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = themeSettings.borderColor;
      ctx.stroke();
      ctx.restore();
      
      // Add text
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(index * angle + angle / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = themeSettings.textColor;
      ctx.font = `${wheelSize.fontSize}px ${themeSettings.fontFamily}`;
      ctx.textShadow = themeSettings.textShadow;
      ctx.fillText(segment.text, radius - 15, 5);
      ctx.restore();
    });
    
    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 20, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = themeSettings.borderColor;
    ctx.stroke();
    
  }, [segments, isSpinning, size, theme]);
  
  return (
    <WheelContainer size={size}>
      <Pointer theme={theme} size={size} />
      <Canvas 
        ref={canvasRef} 
        isSpinning={isSpinning}
        theme={theme}
        spinDegrees={spinDegrees}
        animationId={animationId}
      />
    </WheelContainer>
  );
};

export default SpinningWheel; 